// COS-SLICE1 (docs/design/2026-10-28-chief-of-staff-and-assistants.md §5): the follow-up card
// under a message — "Make this a task?", "Set a reminder?", "Schedule a meeting?" — with the
// parsed title and date, one primary button that opens a modal prefilled for review, and a
// visible Dismiss. Nothing is created until the person confirms; what they confirm is created as
// them (`suggestions.accept`). A meeting card needs the team's series (`defaults.meeting_series_id`);
// without one it is not offered. Agents never get cards (the API lists none for them).
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Textarea,
  toast,
} from "@companyos/ui";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellIcon, CalendarPlusIcon, ListPlusIcon, XIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import { problemText } from "../settings/problem-text";
import { boardsQuery, chatKeys, type Suggestion, suggestionsQuery } from "./chat-queries";
import type { ChatContext } from "./use-chat";

const COPY: Record<
  Suggestion["kind"],
  { question: string; action: string; icon: typeof BellIcon; done: string }
> = {
  task: {
    question: "Make this a task?",
    action: "Create task…",
    icon: ListPlusIcon,
    done: "Task created",
  },
  reminder: {
    question: "Set a reminder?",
    action: "Set reminder…",
    icon: BellIcon,
    done: "Reminder set",
  },
  meeting: {
    question: "Schedule a meeting?",
    action: "Schedule…",
    icon: CalendarPlusIcon,
    done: "Meeting scheduled",
  },
};

/** A card the person can act on: pending, and a meeting only with the team's series. */
export const offered = (s: Suggestion) =>
  s.state === "pending" && (s.kind !== "meeting" || s.defaults.meeting_series_id !== null);

/**
 * The pending cards of the given threads by the message they sit under (one per message). Only
 * for someone who can write here: a read-only viewer acts on none, so none are fetched.
 */
export function useSuggestions(
  chat: ChatContext,
  threadIds: readonly string[],
): ReadonlyMap<string, Suggestion> {
  const on = !chat.readOnly && chat.meId !== null;
  const ids = [...new Set(threadIds.filter(Boolean))];
  const results = useQueries({
    queries: ids.map((id) => ({ ...suggestionsQuery(chat.api, chat.co, id), enabled: on })),
  });
  const out = new Map<string, Suggestion>();
  for (const r of results)
    for (const s of r.data?.data ?? [])
      if (offered(s) && !out.has(s.message_id)) out.set(s.message_id, s);
  return out;
}

/** "Fri 31 Oct, 14:00": the card's date in short form. */
export function shortWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** An ISO instant as a `datetime-local` value ("2026-10-31T14:00", the browser's zone). */
export function toLocalInput(iso: string): string {
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}T${pad(t.getHours())}:${pad(t.getMinutes())}`;
}

/** A `datetime-local` value as an ISO instant, or null when empty or invalid. */
function fromLocalInput(v: string): string | null {
  if (!v) return null;
  const t = new Date(v);
  return Number.isNaN(t.getTime()) ? null : t.toISOString();
}

/** Drops a decided card from its thread's cached list at once. */
function useForget(chat: ChatContext, s: Suggestion) {
  const qc = useQueryClient();
  return () => {
    const key = chatKeys.suggestions(chat.co, s.thread_id);
    qc.setQueryData<{ data: Suggestion[] }>(key, (old) =>
      old ? { data: old.data.filter((x) => x.id !== s.id) } : old,
    );
    void qc.invalidateQueries({ queryKey: key });
  };
}

export function SuggestionCard({
  chat,
  suggestion: s,
}: {
  chat: ChatContext;
  suggestion: Suggestion;
}) {
  const [open, setOpen] = useState(false);
  const forget = useForget(chat, s);
  const dismiss = useMutation({
    mutationFn: () => chat.api.client.suggestions.dismiss({ params: { co: chat.co, id: s.id } }),
    onSuccess: forget,
  });
  const copy = COPY[s.kind];
  const Icon = copy.icon;
  const when = shortWhen(s.proposal.at);
  return (
    <div
      data-slot="suggestion-card"
      data-kind={s.kind}
      className="flex max-w-xl items-center gap-2 rounded-md border border-border-subtle bg-sunken px-3 py-2"
    >
      <Icon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1 text-sm font-medium">
          {copy.question}
          <InfoTip label="About this suggestion">
            Suggested from a date in this message. Nothing is created until you confirm.
          </InfoTip>
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {s.proposal.title}
          {when ? ` · ${when}` : ""}
        </div>
      </div>
      <Button size="xs" onClick={() => setOpen(true)}>
        {copy.action}
      </Button>
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label="Dismiss"
        title="Dismiss"
        disabled={dismiss.isPending}
        onClick={() => dismiss.mutate()}
      >
        <XIcon />
      </Button>
      {open ? <AcceptDialog chat={chat} suggestion={s} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}

type Body = Parameters<ChatContext["api"]["client"]["suggestions"]["accept"]>[0]["body"];

function AcceptDialog({
  chat,
  suggestion: s,
  onClose,
}: {
  chat: ChatContext;
  suggestion: Suggestion;
  onClose: () => void;
}) {
  const { api, co } = chat;
  const qc = useQueryClient();
  const forget = useForget(chat, s);
  const ids = { title: useId(), when: useId(), board: useId() };
  const [title, setTitle] = useState(s.proposal.title);
  const [when, setWhen] = useState(toLocalInput(s.proposal.at));
  const [boardId, setBoardId] = useState("");
  const boards = useQuery({ ...boardsQuery(api, co), enabled: s.kind === "task" });
  const list = boards.data?.data.filter((b) => !b.archived_at) ?? [];
  const board =
    boardId ||
    (s.defaults.board_id && list.some((b) => b.id === s.defaults.board_id)
      ? s.defaults.board_id
      : (list[0]?.id ?? ""));
  const at = fromLocalInput(when);
  const save = useMutation({
    mutationFn: (body: Body) => api.client.suggestions.accept({ params: { co, id: s.id }, body }),
    onSuccess: () => {
      toast.add({ title: COPY[s.kind].done, type: "success" });
      forget();
      void qc.invalidateQueries({ queryKey: chatKeys.messages(co, s.thread_id) });
      onClose();
    },
  });
  const body: Body | null =
    s.kind === "task"
      ? title.trim() && board
        ? { kind: "task", title: title.trim(), due_at: at, board_id: board }
        : null
      : s.kind === "reminder"
        ? title.trim() && at
          ? { kind: "reminder", text: title.trim(), at }
          : null
        : s.defaults.meeting_series_id && at
          ? { kind: "meeting", series_id: s.defaults.meeting_series_id, scheduled_at: at }
          : null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (body) save.mutate(body);
  };
  const heading =
    s.kind === "task" ? "Create task" : s.kind === "reminder" ? "Set reminder" : "Schedule meeting";
  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-5" data-slot="suggestion-accept">
          <DialogHeader>
            <DialogTitle>{heading}</DialogTitle>
          </DialogHeader>
          {s.kind === "meeting" ? (
            <Field>
              <FieldLabel>Series</FieldLabel>
              <p className="text-sm">Team meeting</p>
            </Field>
          ) : (
            <Field>
              <FieldLabel htmlFor={ids.title}>{s.kind === "task" ? "Title" : "Text"}</FieldLabel>
              {s.kind === "task" ? (
                <Input
                  id={ids.title}
                  className="h-10"
                  value={title}
                  maxLength={200}
                  onChange={(e) => setTitle(e.target.value)}
                />
              ) : (
                <Textarea
                  id={ids.title}
                  rows={2}
                  value={title}
                  maxLength={2000}
                  onChange={(e) => setTitle(e.target.value)}
                />
              )}
            </Field>
          )}
          <Field>
            <FieldLabel htmlFor={ids.when}>
              {s.kind === "task" ? "Due" : s.kind === "reminder" ? "At" : "Scheduled at"}
            </FieldLabel>
            <Input
              id={ids.when}
              type="datetime-local"
              className="h-10"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
            />
          </Field>
          {s.kind === "task" ? (
            <Field>
              <FieldLabel htmlFor={ids.board}>Board</FieldLabel>
              {list.length ? (
                <NativeSelect
                  id={ids.board}
                  value={board}
                  onChange={(e) => setBoardId(e.target.value)}
                >
                  {list.map((b) => (
                    <NativeSelectOption key={b.id} value={b.id}>
                      {b.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {boards.isPending ? "Loading boards…" : "No board yet: create one in Work first."}
                </p>
              )}
            </Field>
          ) : null}
          {save.error ? (
            <Notice tone="danger" role="alert">
              {problemText(save.error, "It could not be created.")}
            </Notice>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={!body || save.isPending}>
              {save.isPending ? "Saving…" : heading}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
